import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, DollarSign, Bell, History } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { useGetAllFeeQuery } from "../store/feeApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { useGetAllQuery } from "../store/studentApi";

const FeePending = () => {
  const [expandedRow, setExpandedRow] = React.useState(null);
  const { user } = useSelector((state: RootState) => state.user);
  const { data: fees } = useGetAllFeeQuery(user?.token);
  const { data: allStudents } = useGetAllQuery(user?.token);

  const handleReminder = (std: string) => {
    const studentDetail = allStudents.find((item) => item._id === std);
    if (studentDetail) {
      const currentDate = new Date().toLocaleDateString("en-GB"); // DD/MM/YYYY

      const Message = `*Dear ${studentDetail?.fname},*

This is a gentle reminder to complete your *${studentDetail?.course}* fee payment by *${currentDate}*.

Keeping your fee updated ensures your academic progress continues smoothly.

If you need any assistance, feel free to contact us at *+91 7400844492*.

*Your success is our priority.*

*Best regards,*  
 Robotwala`;

      const phoneNumber = studentDetail.contact;

      // WhatsApp direct send URL
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        Message,
      )}`;

      // Open WhatsApp directly (new tab recommended)
      window.open(whatsappUrl, "_blank");
    } else {
      console.log("Student not found");
    }
  };

  const totalPayable = fees?.reduce((sum, fees) => sum + fees.payable, 0);
  const totalPaid = fees?.reduce((sum, fee) => sum + fee.paid, 0);
  const totalPending = totalPayable - totalPaid;

  const isOlderThan35Days = (dateStr: string) => {
    const lastDate = new Date(dateStr);
    const now = new Date();
    const diffInTime = now.getTime() - lastDate.getTime();
    const diffInDays = diffInTime / (1000 * 3600 * 24);
    return diffInDays > 35;
  };
  const filteredFees =
    fees?.filter(
      (student) =>
        student.paid < student.payable &&
        isOlderThan35Days(student.lastRecordDate),
    ) || [];
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 10;

  // Calculate paginated data
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData =
    filteredFees?.slice(indexOfFirstItem, indexOfLastItem) || [];

  // Handle page navigation
  const handleNextPage = () => {
    if (currentPage < Math.ceil((filteredFees?.length || 0) / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const ProgressBar = ({ progress }) => (
    <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-2">
      <div
        className="bg-[#009F3C] dark:bg-foreground h-2 rounded-full transition-all duration-500 ease-in-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <Card className="w-full">
        {/* Rest of the card header remains the same */}
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background text-foreground">
                <tr>
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                    Student
                  </th>
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                    Course Details
                  </th>
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                    Payment Status
                  </th>
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                    Pending/Total
                  </th>
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y ">
                {currentData?.map((student, index) => (
                  <React.Fragment key={index}>
                    <tr
                      className={`hover:bg-muted cursor-pointer transition-colors ${
                        expandedRow === index ? "bg-gray-50" : ""
                      }`}
                      onClick={() =>
                        setExpandedRow(expandedRow === index ? null : index)
                      }
                    >
                      <td className="p-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex flex-col">
                            <span className="text-sm  text-foreground">
                              {student.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {student.bCode}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-foreground">
                          {student.course}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="w-40">
                          <ProgressBar
                            progress={(student.paid / student.payable) * 100}
                          />
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="w-40">
                          {student.paid}/{student.payable}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                // variant="default"
                                className="bg-red-500 hover:bg-red-900 text-white text-sm group relative overflow-hidden dark:overflow-visible"
                              >
                                <span className="absolute inset-0 w-3 bg-[#009F3C] dark:bg-background transition-all duration-[250ms] ease-out group-hover:w-full"></span>
                                <span className="relative flex items-center gap-1 group-hover:text-white">
                                  <Bell className="h-4 w-4" />
                                  Send Reminder
                                </span>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  <span className="text-foreground">
                                    {" "}
                                    Send Fee Reminder
                                  </span>
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will send a fee reminder notification to{" "}
                                  {student.name}. The pending amount is ₹
                                  {student.payable - student.paid}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="text-muted-foreground">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-[#009F3C] hover:bg-[#009F3C]"
                                  onClick={() =>
                                    handleReminder(student.student)
                                  }
                                >
                                  Send Reminder
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {/* <FeeHistoryModal student={student} /> */}
                        </div>
                      </td>
                      {/* <td className="p-4">
                        <FeeHistoryModal student={student} />
                      </td> */}
                    </tr>
                    {/* {expandedRow === index && (
                      <tr className="bg-gray-50">
                        <td colSpan={4}>
                          <div className="p-4">Additional information here</div>
                        </td>
                      </tr>
                    )} */}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div>
                Page {currentPage} of{" "}
                {Math.ceil((fees?.length || 0) / itemsPerPage)}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={
                  currentPage >= Math.ceil((fees?.length || 0) / itemsPerPage)
                }
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeePending;
